import { useCallback, useEffect, useMemo, useState } from "react";
import { getCookie } from "../../../common/utils";
import {
  IAdjustTimeAndTextState,
  IAdjustTimeAndTextStateRef,
  ICreateEditAlertPayload,
  IEditAlertState,
  IGroupAlerts,
  ISelectGroupState,
} from "../../../common/types/alertsTypes";
import { useDispatch, useSelector } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { createAlert } from "./createAlertSlice";
import useNotification from "../../notification/useNotification";
import { useSpinner } from "../../../customHooks";
import { IAlerts } from "../../../pages/alerts/utils";
import { IAlertTypes } from "../../../common/types/alertsTypes";
import { createQuickAlerts } from "../../../pages/alerts/quickAlerts/createQuickAlerts/createQuickAlertsSlice";
import {
  selector as alertsApi,
  fetchAlertsData,
  setNewlyCreatedAlertId,
} from "../../../pages/alerts/listAlertsSlice";
import {
  selector as editAlertSelector,
  setEditAlertData,
  editAlert,
} from "./editAlertSlice";
import {
  selector as editQuickAlertSelector,
  setEditQuickAlertData,
  editQuickAlert,
} from "../../../pages/alerts/quickAlerts/editQuickAlerts/editQuickAlertSlice";
import {
  selector as createEditAlertModalSelector,
  actions as modalActions,
} from "./createEditAlertModalSlice";
import { isSuccessResponse } from "../../../common/utils";
import { IInitialSliceState } from "../../../common/types/commonTypes";
import { groups } from "../../../store/reducers";
import {
  fetchQuickAlertsData,
  selector as listQuickAlertsApi,
} from "../../../pages/alerts/quickAlerts/listQuickAlerts/listQuickAlertsSlice";
import { QUICK_ALERT_PAGE_LIMIT, Status } from "../../../common/constants";
import { getQuickAlertButtonLabelDuplicateMessage } from "../../../pages/alerts/quickAlerts/quickAlertButtonLabelDuplicate";
import {
  getTemplates,
  getOrgTemplates,
  mergeOrgTemplates,
  selector as templatesSelector,
} from "../../superAdmin/templatesSlice";
import { ITemplate } from "../../../common/types/templateTypes";
import {
  isColorVariantSelected,
  templateToAlertType,
  getDisplayTemplates,
} from "../../../common/templateColorVariants";

interface ICreateAlertResponseState {
  createAlert: IInitialSliceState;
  editAlert: IEditAlertState;
}

interface IConfirmationBoxText {
  groups: {
    id: string;
    displayName: string;
  }[];
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  isSendNow: boolean;
}

const useCreateAlert = () => {
  const {
    isActive: isModalActive,
    isEditMode: isEdit,
    isQuickAlert: isQuickAlertMode,
    isEditQuickAlert: isEditQuickAlertMode,
  } = useSelector(createEditAlertModalSelector);

  const dispatch: ThunkDispatch<ICreateAlertResponseState, undefined, Action> =
    useDispatch();
  const { alertData } = useSelector(editAlertSelector);
  const { editQuickAlertData } = useSelector(editQuickAlertSelector);
  const editQuickAlertsResponse = useSelector(editQuickAlertSelector);
  const listQuickAlertsState = useSelector(listQuickAlertsApi);
  const SELECTED_GROUP_TAB_NUMBER: number = 2;
  const [confirmationBoxText, setConfirmationBoxText] =
    useState<IConfirmationBoxText>();
  // TODO: backend tech debt: check db schema types and remove number conversion
  const initialSelectedTemplate: number = isEdit
    ? Number(alertData?.templateId)
    : 0;
  const initialSelectedTemplateData: IAlertTypes = {
    id: 0,
    name: "",
    templateReqHeading: false,
    templateReqMessage: false,
    templateReqIcon: false,
    templateReqLogo: false,
    templateReqZipCode: false,
    orgIds: [],
  };
  const [selectedTemplate, setSelectedTemplate] = useState<IAlertTypes>(
    initialSelectedTemplateData,
  );
  const handleTemplateClick = (templateData: IAlertTypes) => {
    const newSelectedTemplate = templateData;
    setSelectedTemplate(newSelectedTemplate);
    handleTemplateIdChange(templateData?.id);
  };

  const isTemplateDivSelected = (template: IAlertTypes) => {
    if (isColorVariantSelected(template.id!, selectedTemplate?.id ?? -1)) {
      return true;
    }
    return selectedTemplate?.id === template.id;
  };

  const alertsData: IAlerts = useSelector(alertsApi);
  const createAlertResponse = useSelector(
    (state: ICreateAlertResponseState) => state.createAlert,
  );
  const editAlertResponse = useSelector(
    (state: ICreateAlertResponseState) => state.editAlert,
  );
  const [templateId, setTemplateId] = useState<number>(initialSelectedTemplate);

  const { templates: allTemplates, orgTemplates, status: templatesStatus } =
    useSelector(templatesSelector);

  const availableTemplates = useMemo(() => {
    const orgId = getCookie("organizationId") || "";
    if (!orgId) return allTemplates.filter((t) => t.public);
    return mergeOrgTemplates(
      allTemplates.filter((t) => t.public),
      orgTemplates,
    );
  }, [allTemplates, orgTemplates]);

  const displayTemplates = useMemo(
    () => getDisplayTemplates(availableTemplates),
    [availableTemplates],
  );

  const templateLookup = useMemo(() => {
    const map = new Map<number, ITemplate>();
    availableTemplates.forEach((t) => map.set(t.templateId, t));
    return map;
  }, [availableTemplates]);

  const [isEmergencyAlert, setIsEmergencyAlert] = useState<boolean>(true);
  const [saveAsQuickAlert, setSaveAsQuickAlert] = useState<boolean>(false);
  const [buttonLabelText, setButtonLabelText] = useState<string>("");
  const [quickAlertId, setQuickAlertId] = useState<string>("");
  const [allClearChecked, setAllClearChecked] = useState<boolean>();
  const [allClearTime, setAllClearTime] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isTemplateTabFilled, setIsTemplateTabFilled] = useState<boolean>();
  const [isDateTimeTabFilled, setIsDateTimeTabFilled] =
    useState<boolean>(false);
  const [isSelectGroupsTabFilled, setIsSelectGroupsTabFilled] =
    useState<boolean>(false);

  const initialAdjustTimeAndTextState: IAdjustTimeAndTextState = {
    startDateState: new Date(""),
    endDateState: new Date(""),
    startTimeState: "",
    endTimeState: "",
    timeZoneState: "",
    severityLevelState: "",
    alertMessageState: "",
    alertHeadingState: "",
    startUTCDate: "",
    endUTCDate: "",
    isCheckedNowState: false,
    startDateString: "",
    endDateString: "",
  };

  const [adjustTimeAndTextState, setAdjustTimeAndTextState] =
    useState<IAdjustTimeAndTextState>(initialAdjustTimeAndTextState);

  const initialSelectGroupState: ISelectGroupState = {
    groups: [],
    checked: [],
    selectAllChecked: false,
  };

  const [selectGroupState, setSelectGroupState] = useState<ISelectGroupState>(
    initialSelectGroupState,
  );

  useSpinner(createAlertResponse.status);
  useSpinner(editAlertResponse.status);
  useSpinner(editQuickAlertsResponse.status);
  useSpinner(templatesStatus);
  let editSelectGroupState: ISelectGroupState;
  let editAdjustDateTimeState: IAdjustTimeAndTextState;
  let adjustTimeAndTextStateRef: IAdjustTimeAndTextStateRef = {
    updateAdjustTimeAndTextState: undefined,
  };

  const handleSelectedTab = (index: number): void => {
    setSelectedTab(index);
    if (
      index == SELECTED_GROUP_TAB_NUMBER &&
      adjustTimeAndTextStateRef?.updateAdjustTimeAndTextState
    ) {
      adjustTimeAndTextStateRef.updateAdjustTimeAndTextState();
    }
  };

  const getDateString = (event: string) => {
    const dateObject: Date = new Date(event);
    const day: string = dateObject.getDate().toString().padStart(2, "0");
    const month: string = (dateObject.getMonth() + 1)
      .toString()
      .padStart(2, "0");
    const year: number = dateObject.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const extractTime = (date: string) => {
    const dateObject = new Date(date);
    const hours = dateObject.getHours().toString().padStart(2, "0");
    const minutes = dateObject.getMinutes().toString().padStart(2, "0");
    const formattedTime = `${hours}:${minutes}`;
    return formattedTime;
  };

  const getEditSelectedGroups = (): IGroupAlerts[] => {
    const selectedGroupsMap = new Map<string, IGroupAlerts>();

    const addGroup = (groupId?: string, displayName?: string) => {
      if (!groupId || !displayName) {
        return;
      }

      if (!selectedGroupsMap.has(groupId)) {
        selectedGroupsMap.set(groupId, {
          id: groupId,
          displayName,
        });
      }
    };

    const alertDataGroups = Array.isArray((alertData as any)?.groups)
      ? (alertData as any).groups
      : [];

    alertDataGroups.forEach((group) => {
      addGroup(
        group?.id || group?.groupId,
        group?.displayName || group?.groupName,
      );
    });

    if (!selectedGroupsMap.size) {
      const matchingAlertRows =
        alertsData?.alerts?.filter(
          (item) => item?.alertId === alertData?.alertId,
        ) || [];

      matchingAlertRows.forEach((item: any) => {
        if (Array.isArray(item?.groups)) {
          item.groups.forEach((group) => {
            addGroup(
              group?.id || group?.groupId,
              group?.displayName || group?.groupName,
            );
          });

          return;
        }

        addGroup(item?.groupId, item?.groupName);
      });
    }

    return Array.from(selectedGroupsMap.values());
  };

  useEffect(() => {
    if (alertData && isEdit) {
      const groups = getEditSelectedGroups();
      const groupIds = groups.map((group) => group.id);

      editAdjustDateTimeState = {
        startDateState: new Date(alertData?.alertDetails[0]?.startDateTime),
        endDateState: new Date(alertData?.alertDetails[0]?.endDateTime),
        startTimeState: extractTime(alertData?.alertDetails[0]?.startDateTime),
        endTimeState: extractTime(alertData?.alertDetails[0]?.endDateTime),
        timeZoneState: alertData?.alertDetails[0]?.timeZone,
        severityLevelState: alertData?.alertDetails[0]?.severity,
        alertMessageState: alertData?.alertDetails[0]?.alertMessage,
        alertHeadingState: alertData?.alertDetails[0]?.alertHeading,
        startUTCDate: alertData?.alertDetails[0]?.startDateTime,
        endUTCDate: alertData?.alertDetails[0]?.endDateTime,
        isCheckedNowState: false,
        startDateString: getDateString(
          alertData?.alertDetails[0]?.startDateTime,
        ),
        endDateString: getDateString(alertData?.alertDetails[0]?.endDateTime),
      };

      editSelectGroupState = {
        groups: groups,
        checked: groupIds,
        selectAllChecked: false,
      };
    }
  }, [alertData, isEdit, alertsData]);

  useEffect(() => {
    if (isEditQuickAlertMode && editQuickAlertData) {
      const groupIds = editQuickAlertData?.groups?.map((group) => group.id);

      editAdjustDateTimeState = {
        startDateState: null,
        endDateState: null,
        startTimeState: null,
        endTimeState: null,
        timeZoneState: "",
        severityLevelState: editQuickAlertData?.severity,
        alertMessageState: editQuickAlertData?.alertMessage,
        alertHeadingState: editQuickAlertData?.alertTitle,
        startUTCDate: null,
        endUTCDate: null,
        isCheckedNowState: false,
        startDateString: "",
        endDateString: "",
      };
      setButtonLabelText(editQuickAlertData?.alertButtonName);
      setQuickAlertId(editQuickAlertData?._id);
      editSelectGroupState = {
        groups: editQuickAlertData?.groups,
        checked: groupIds,
        selectAllChecked: false,
      };
    }
  }, [isEditQuickAlertMode, editQuickAlertData, alertsData]);

  useEffect(() => {
    if (isModalActive || isEdit) {
      handleShow();
    }
  }, [isModalActive, isEdit]);

  const createAdjustTimeAndTextState: IAdjustTimeAndTextState = {
    startDateState: null,
    endDateState: null,
    startTimeState: "",
    endTimeState: "",
    timeZoneState: "MST",
    severityLevelState: "",
    alertMessageState: "",
    alertHeadingState: "",
    startUTCDate: "",
    endUTCDate: "",
    isCheckedNowState: false,
    startDateString: "",
    endDateString: "",
  };

  const createSelectGroupState: ISelectGroupState = {
    groups: [],
    checked: [],
    selectAllChecked: false,
  };

  const resetFields = useCallback(() => {
    const initialSelectGroupState =
      isEdit || isEditQuickAlertMode
        ? editSelectGroupState
        : createSelectGroupState;

    const initialAdjustTimeAndTextState =
      isEdit || isEditQuickAlertMode
        ? editAdjustDateTimeState
        : createAdjustTimeAndTextState;

    let templateToUse =
      isEdit || isEditQuickAlertMode
        ? selectedTemplate
        : initialSelectedTemplateData;

    if (isEdit && alertData?.templateId) {
      const found = templateLookup.get(Number(alertData.templateId));
      if (found) templateToUse = templateToAlertType(found);
    }

    if (isEditQuickAlertMode && editQuickAlertData?.templateId) {
      const found = templateLookup.get(Number(editQuickAlertData.templateId));
      if (found) templateToUse = templateToAlertType(found);
      setButtonLabelText(editQuickAlertData?.alertButtonName);
      setQuickAlertId(editQuickAlertData?._id);
      if (editQuickAlertData?.allClearTime?.trim()) {
        setAllClearChecked(true);
        setAllClearTime(editQuickAlertData.allClearTime);
      }
    } else {
      setAllClearChecked(false);
      setAllClearTime("");
    }
    setSelectedTab(0);
    setButtonLabelText(editQuickAlertData?.alertButtonName || "");
    setTemplateId(templateToUse?.id || 0);
    setIsDateTimeTabFilled(false);
    setIsSelectGroupsTabFilled(false);
    setAdjustTimeAndTextState(initialAdjustTimeAndTextState);
    setSelectedTemplate(templateToUse);
    setSelectGroupState(initialSelectGroupState);
    handleTemplateClick(templateToUse);
    setSaveAsQuickAlert(false);
  }, [isEdit, isEditQuickAlertMode]);

  const handleClose = () => {
    dispatch(modalActions.hideModal());
    if (isEdit) {
      dispatch(setEditAlertData(createAdjustTimeAndTextState));
    } else if (isEditQuickAlertMode) {
      dispatch(setEditQuickAlertData(null));
    }
  };
  const handleShow = () => {
    resetFields();
    const orgId = getCookie("organizationId") || "";
    dispatch(getTemplates());
    if (orgId) {
      dispatch(getOrgTemplates(orgId));
    }
  };

  const handleEmergencyTabSelected = (isSelected: boolean) => {
    setIsEmergencyAlert(isSelected);
    resetFields();
  };

  const handleTemplateIdChange = (templateId: number) => {
    setTemplateId(templateId);
  };

  const handleNextTab = () => {
    setSelectedTab((prevTab) => prevTab + 1);
    if (selectedTab == 2) {
      setConfirmationBoxText({
        groups: selectGroupState.groups,
        startTime: adjustTimeAndTextState.startTimeState,
        endTime: adjustTimeAndTextState.endTimeState,
        startDate: adjustTimeAndTextState.startDateString,
        endDate: adjustTimeAndTextState.endDateString,
        timeZone: adjustTimeAndTextState.timeZoneState,
        isSendNow: adjustTimeAndTextState.isCheckedNowState,
      });
    }
  };

  useEffect(() => {
    templateId ? setIsTemplateTabFilled(true) : setIsTemplateTabFilled(false);
  }, [templateId]);

  const getPayload = (): ICreateEditAlertPayload => {
    const createAlertPayload: ICreateEditAlertPayload = {
      startDateTime: adjustTimeAndTextState.startUTCDate,
      endDateTime: adjustTimeAndTextState.endUTCDate,
      source: "MAN",
      timeZone: adjustTimeAndTextState?.timeZoneState,
      severity: adjustTimeAndTextState?.severityLevelState,
      alertMessage: adjustTimeAndTextState?.alertMessageState,
      alertHeading: adjustTimeAndTextState?.alertHeadingState,
      templateId: templateId,
      groups: selectGroupState.groups,
    };
    if (allClearChecked) {
      createAlertPayload.allClearTime = allClearTime;
    }
    return createAlertPayload;
  };

  const getEditAlertQueryParams = () => {
    return `alertId=${alertData?.alertId}`;
  };

  const handleDateTimeFieldEmpty = (value: boolean) => {
    setIsDateTimeTabFilled(!value);
  };

  const handleSelectGroupFieldEmpty = (value: boolean) => {
    setIsSelectGroupsTabFilled(!value);
  };

  const quickAlertsValues = listQuickAlertsState.quickalerts?.values;
  const hasCompleteQuickAlertsForValidation =
    listQuickAlertsState.status === Status.SUCCESS &&
    Array.isArray(quickAlertsValues) &&
    (quickAlertsValues.length === 0 ||
      listQuickAlertsState.quickalerts?.total === quickAlertsValues.length);

  const isQuickAlertsValidationLoading =
    (isQuickAlertMode || isEditQuickAlertMode || saveAsQuickAlert) &&
    !hasCompleteQuickAlertsForValidation &&
    listQuickAlertsState.status === Status.PENDING;

  useEffect(() => {
    if (
      isModalActive &&
      selectedTab === 3 &&
      (isQuickAlertMode || isEditQuickAlertMode || saveAsQuickAlert) &&
      !hasCompleteQuickAlertsForValidation &&
      listQuickAlertsState.status !== Status.PENDING
    ) {
      dispatch(fetchQuickAlertsData(undefined));
    }
  }, [
    dispatch,
    isModalActive,
    selectedTab,
    isQuickAlertMode,
    isEditQuickAlertMode,
    saveAsQuickAlert,
    hasCompleteQuickAlertsForValidation,
    listQuickAlertsState.status,
  ]);

  const quickAlertButtonLabelError = useMemo(() => {
    const needsLabelCheck =
      isQuickAlertMode || isEditQuickAlertMode || saveAsQuickAlert;
    if (
      !isModalActive ||
      !needsLabelCheck ||
      !hasCompleteQuickAlertsForValidation
    ) {
      return null;
    }
    return getQuickAlertButtonLabelDuplicateMessage({
      name: buttonLabelText,
      groups: selectGroupState.groups ?? [],
      existingAlerts: quickAlertsValues,
      excludeAlertId: isEditQuickAlertMode ? editQuickAlertData?._id : null,
    });
  }, [
    isModalActive,
    isQuickAlertMode,
    isEditQuickAlertMode,
    saveAsQuickAlert,
    hasCompleteQuickAlertsForValidation,
    buttonLabelText,
    selectGroupState.groups,
    quickAlertsValues,
    editQuickAlertData?._id,
  ]);

  const { showSuccessNotification, showWarningNotification } =
    useNotification();
  const dispatchCreateOrUpdateAlert = (
    action,
    payload,
    queryParams?,
  ): Promise<{ message: string; status: string }> => {
    return dispatch(action(payload, queryParams))
      .then((response: any) => {
        if (isSuccessResponse(response.payload)) {
          dispatch(fetchAlertsData({ pageIndex: 0, pageSize: 10 })).then(
            (response) => {
              dispatch(setNewlyCreatedAlertId("0"));
              setTimeout(() => {
                dispatch(setNewlyCreatedAlertId(null));
              }, 8000);
            },
          );
          return { status: "success", message: response.payload.message };
        } else {
          const errorMessage: string =
            response.payload.response?.data?.message ||
            response.payload.message;
          return { status: "error", message: errorMessage };
        }
      })
      .catch((error: any) => {
        console.error("Error:", error);
        return { status: "error", message: "Unexpected error occurred" };
      });
  };

  const dispatchCreateQuickAlerts = (
    action,
    payload,
  ): Promise<{ message: string; status: string }> => {
    return dispatch(action(payload))
      .then((response: any) => {
        if (isSuccessResponse(response.payload)) {
          return { status: "success", message: response.payload.message };
        } else {
          return {
            status: "error",
            message:
              response.payload.response?.data?.message || "Unknown error",
          };
        }
      })
      .catch((error: any) => {
        console.error("Error:", error);
        return { status: "error", message: "Unexpected error occurred" };
      });
  };

  const dispatchEditQuickAlerts = (action, payload) => {
    try {
      dispatch(action(payload)).then((response) => {
        if (isSuccessResponse(response.payload)) {
          showSuccessNotification(response.payload.message);
        } else {
          showWarningNotification(response.payload.response.data.message);
        }
        dispatch(
          fetchQuickAlertsData({ page: 1, limit: QUICK_ALERT_PAGE_LIMIT }),
        );
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const showNotification = (message: string, type: string) => {
    type === "success"
      ? showSuccessNotification(message)
      : showWarningNotification(message);
  };

  const createResponseMessage = (
    createAlertResponse: any,
    saveQuickalertResponse: any,
  ) => {
    let responseMessage = { message: "", status: "success" };
    if (createAlertResponse && saveQuickalertResponse) {
      if (
        createAlertResponse.status === "success" &&
        saveQuickalertResponse.status === "success"
      ) {
        responseMessage = {
          message:
            createAlertResponse.message.slice(0, -1) +
            " and " +
            saveQuickalertResponse.message,
          status: "success",
        };
      } else if (
        createAlertResponse.status === "error" &&
        saveQuickalertResponse.status === "error"
      ) {
        responseMessage = {
          message: `Alert creation failed due to : ${createAlertResponse.message.slice(
            0,
            -1,
          )} and save quick alert failed due to : ${
            saveQuickalertResponse.message
          }`,
          status: "error",
        };
      } else if (
        createAlertResponse.status === "error" &&
        saveQuickalertResponse.status === "success"
      ) {
        responseMessage = {
          message: `${saveQuickalertResponse.message.slice(
            0,
            -1,
          )} but alert creation failed : ${createAlertResponse.message}`,
          status: "error",
        };
      } else if (
        createAlertResponse.status === "success" &&
        saveQuickalertResponse.status === "error"
      ) {
        responseMessage = {
          message: `${createAlertResponse.message.slice(
            0,
            -1,
          )} but quick alert save failed : ${saveQuickalertResponse.message}`,
          status: "error",
        };
      }
    } else if (createAlertResponse) {
      responseMessage = {
        message: createAlertResponse.message,
        status: createAlertResponse.status,
      };
    } else if (saveQuickalertResponse) {
      responseMessage = {
        message: saveQuickalertResponse.message,
        status: saveQuickalertResponse.status,
      };
    }
    return responseMessage;
  };

  const handleQuickAlert = () => {
    if (quickAlertButtonLabelError || isQuickAlertsValidationLoading) {
      return;
    }
    const payload = getPayload();
    const quickAlertPayload = {
      templateId: payload.templateId,
      alertTitle: payload.alertHeading,
      alertMessage: payload.alertMessage,
      severity: payload.severity,
      groups: payload.groups,
      source: "MAN",
      alertButtonName: buttonLabelText,
      ...(allClearChecked && allClearTime?.trim() && { allClearTime }),
    };

    dispatchCreateQuickAlerts(createQuickAlerts, quickAlertPayload).then(
      (saveQuickalertResponse) => {
        showNotification(
          saveQuickalertResponse?.message,
          saveQuickalertResponse?.status,
        );
      },
    );

    handleClose();
  };

  const handleEditQuickAlert = () => {
    if (quickAlertButtonLabelError || isQuickAlertsValidationLoading) {
      return;
    }
    const payload = getPayload();
    const editQuickAlertPayload = {
      templateId: payload.templateId,
      alertTitle: payload.alertHeading,
      alertMessage: payload.alertMessage,
      severity: payload.severity,
      groups: payload.groups,
      source: "MAN",
      alertButtonName: buttonLabelText,
      alertId: editQuickAlertData._id,
      ...(allClearChecked && allClearTime?.trim() && { allClearTime }),
    };
    dispatchEditQuickAlerts(editQuickAlert, editQuickAlertPayload);
    handleClose();
  };

  const handleCreateAlert = () => {
    if (
      saveAsQuickAlert &&
      (quickAlertButtonLabelError || isQuickAlertsValidationLoading)
    ) {
      return;
    }
    const payload = getPayload();
    let queryParams;
    if (isEdit) {
      queryParams = getEditAlertQueryParams();
      const jobIds: string[] = alertsData?.alerts
        ?.filter((item) => item?.alertId === alertData?.alertId)
        .map((item) => item.jobId);
      payload.jobIds = jobIds;
    }

    const createAlertResponse = isEdit
      ? dispatchCreateOrUpdateAlert(editAlert, { payload, queryParams })
      : dispatchCreateOrUpdateAlert(createAlert, payload);

    if (saveAsQuickAlert) {
      const quickAlertPayload = {
        templateId: payload.templateId,
        alertTitle: payload.alertHeading,
        alertMessage: payload.alertMessage,
        severity: payload.severity,
        groups: payload.groups,
        source: "MAN",
        alertButtonName: buttonLabelText,
      };
      if (allClearChecked) {
        quickAlertPayload["allClearTime"] = allClearTime;
      }
      createAlertResponse.then((createAlertResponse) => {
        dispatchCreateQuickAlerts(createQuickAlerts, quickAlertPayload).then(
          (saveQuickalertResponse) => {
            const responsMessage = createResponseMessage(
              createAlertResponse,
              saveQuickalertResponse,
            );
            showNotification(responsMessage.message, responsMessage.status);
          },
        );
      });
    } else {
      createAlertResponse.then((createAlertResponse) => {
        showNotification(
          createAlertResponse.message,
          createAlertResponse.status,
        );
      });
    }
    handleClose();
  };

  const getTabClassName = (tabIndex: number): string => {
    const tabClassName: string =
      tabIndex < selectedTab
        ? "react-tabs__tab react-tabs__tab--success"
        : tabIndex === selectedTab
          ? "react-tabs__tab react-tabs__tab--selected"
          : "react-tabs__tab";
    return tabClassName;
  };

  const getFormClassName = (tabIndex: number): string => {
    const tabClassName: string = tabIndex < selectedTab ? "success" : "";
    return tabClassName;
  };

  return {
    handleClose,
    isEmergencyAlert,
    selectedTab,
    setSelectedTab,
    isTemplateTabFilled,
    displayTemplates,
    templateLookup,
    isDateTimeTabFilled,
    isSelectGroupsTabFilled,
    selectedTemplate,
    handleEmergencyTabSelected,
    handleTemplateIdChange,
    handleNextTab,
    handleDateTimeFieldEmpty,
    handleCreateAlert,
    handleQuickAlert,
    handleEditQuickAlert,
    adjustTimeAndTextState,
    setAdjustTimeAndTextState,
    selectGroupState,
    setSelectGroupState,
    handleSelectGroupFieldEmpty,
    handleTemplateClick,
    isTemplateDivSelected,
    confirmationBoxText,
    getTabClassName,
    getFormClassName,
    handleSelectedTab,
    adjustTimeAndTextStateRef,
    isModalActive,
    isEdit,
    isQuickAlertMode,
    isEditQuickAlertMode,
    saveAsQuickAlert,
    setSaveAsQuickAlert,
    buttonLabelText,
    setButtonLabelText,
    allClearChecked,
    allClearTime,
    setAllClearTime,
    setAllClearChecked,
    quickAlertButtonLabelError,
    isQuickAlertsValidationLoading,
  };
};

export default useCreateAlert;
